import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark";
export const PALETTE_IDS = ["classic", "refined", "maroon", "forest", "bottle", "plum", "slate"] as const;
export type Palette = (typeof PALETTE_IDS)[number];
// A=Photo, B=Split, C=Editorial, D=Poster, E=Mosaic
export const HERO_IDS = ["A", "B", "C", "D", "E"] as const;
export type HeroVariant = (typeof HERO_IDS)[number];

export interface Tweaks {
  theme: Theme;
  palette: Palette;
  hero: HeroVariant;
}

const STORAGE_KEY = "biet-tweaks";

const DEFAULTS: Tweaks = {
  theme: "light",
  palette: "refined",
  hero: "D",
};

function readFromStorage(): Tweaks {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return {
      theme: raw.theme === "dark" ? "dark" : "light",
      palette:
        typeof raw.palette === "string" && (PALETTE_IDS as readonly string[]).includes(raw.palette)
          ? (raw.palette as Palette)
          : DEFAULTS.palette,
      hero:
        typeof raw.hero === "string" && (HERO_IDS as readonly string[]).includes(raw.hero)
          ? (raw.hero as HeroVariant)
          : DEFAULTS.hero,
    };
  } catch {
    return DEFAULTS;
  }
}

let state: Tweaks = readFromStorage();
const subscribers = new Set<() => void>();

function applyToRoot(t: Tweaks) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  html.setAttribute("data-theme", t.theme);
  html.setAttribute("data-palette", t.palette);
}

function persist(t: Tweaks) {
  if (typeof window === "undefined") return;
  try {
    // Merge with any other tweak keys we don't manage so we don't clobber them.
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...raw, ...t }));
  } catch {
    /* ignore */
  }
}

// Apply the initial state to the root on first import (paint already
// happened via index.html bootstrap script — this just re-syncs).
applyToRoot(state);

function emit() {
  subscribers.forEach((cb) => cb());
}

export const tweaksStore = {
  get(): Tweaks {
    return state;
  },
  set<K extends keyof Tweaks>(key: K, value: Tweaks[K]) {
    if (state[key] === value) return;
    state = { ...state, [key]: value };
    applyToRoot(state);
    persist(state);
    emit();
  },
  subscribe(cb: () => void): () => void {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  },
};

/** React hook — subscribes to the tweaks store. */
export function useTweaks(): Tweaks {
  return useSyncExternalStore(
    tweaksStore.subscribe,
    tweaksStore.get,
    () => DEFAULTS, // SSR snapshot
  );
}
